import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';

const PARTY_OPTIONS = ['PBS', 'Top Peak IT', 'Rain Bird US', 'Comwire IT'];

const INVOICE_PUBLIC_BASE =
  'https://ufexootemmnvxkqgzpsi.supabase.co/storage/v1/object/public/invoices/';

const fieldProps = { size: 'medium', fullWidth: true };

function invoicePublicUrl(storagePath) {
  if (!storagePath) return '';
  return `${INVOICE_PUBLIC_BASE}${storagePath}`;
}

export default function TransactionsPage() {
  const [form, setForm] = useState({
    from_who: '',
    to_who: '',
    amount: '',
    wise_transaction_amount: '',
    payment_date: '',
    project_cost: false,
    paid: false,
    description: '',
    invoice_url: ''
  });

  const [selectedId, setSelectedId] = useState(null);
  const [detailTx, setDetailTx] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [sortAsc, setSortAsc] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const fetchTransactions = async () => {
      let { data, error } = await supabase.from('transactions').select('*');
      if (!error) setTransactions(data);
    };
    fetchTransactions();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortAsc]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(transactions.length / itemsPerPage));
    setCurrentPage((p) => Math.min(p, tp));
  }, [transactions.length, itemsPerPage]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'project_cost') {
      setForm((f) => ({ ...f, project_cost: value === 'true' }));
      return;
    }
    if (type === 'checkbox') {
      setForm((f) => ({ ...f, [name]: checked }));
      return;
    }
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const sanitizedForm = {
      ...form,
      amount: form.amount === '' ? null : Number(form.amount),
      wise_transaction_amount: form.wise_transaction_amount === '' ? null : Number(form.wise_transaction_amount)
    };

    if (isEditing && selectedId) {
      const { error } = await supabase.from('transactions').update(sanitizedForm).eq('id', selectedId);
      error ? toast.error('Error updating transaction: ' + error.message) : toast.success('Transaction updated!');
    } else {
      const { error } = await supabase.from('transactions').insert([sanitizedForm]);
      error ? toast.error('Error saving transaction: ' + error.message) : toast.success('Transaction saved!');
    }

    setForm({
      from_who: '',
      to_who: '',
      amount: '',
      wise_transaction_amount: '',
      payment_date: '',
      project_cost: false,
      paid: false,
      description: '',
      invoice_url: ''
    });
    setIsEditing(false);
    setSelectedId(null);
    setDetailTx(null);

    const { data } = await supabase.from('transactions').select('*');
    setTransactions(data);
  };

  const sortedTransactions = [...transactions].sort((a, b) => {
    const dateA = new Date(a.payment_date);
    const dateB = new Date(b.payment_date);
    return sortAsc ? dateA - dateB : dateB - dateA;
  });

  const totalPages = Math.max(1, Math.ceil(sortedTransactions.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTransactions = sortedTransactions.slice(startIndex, startIndex + itemsPerPage);

  return (
    <Stack spacing={3}>
      <ToastContainer position="top-right" autoClose={3000} />
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" component="h2" gutterBottom fontWeight={700}>
          Transactions
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                {...fieldProps}
                select
                label="From who"
                name="from_who"
                value={form.from_who}
                onChange={handleChange}
              >
                <MenuItem value="">Select</MenuItem>
                {PARTY_OPTIONS.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                {...fieldProps}
                select
                label="To who"
                name="to_who"
                value={form.to_who}
                onChange={handleChange}
              >
                <MenuItem value="">Select</MenuItem>
                {PARTY_OPTIONS.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                {...fieldProps}
                type="number"
                label="Amount"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                inputProps={{ step: 'any' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                {...fieldProps}
                type="number"
                label="Wise transaction amount"
                name="wise_transaction_amount"
                value={form.wise_transaction_amount}
                onChange={handleChange}
                inputProps={{ step: 'any' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                {...fieldProps}
                type="date"
                label="Payment date"
                name="payment_date"
                value={form.payment_date}
                onChange={handleChange}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'flex-end' }}
                sx={{ height: '100%' }}
              >
                <TextField
                  {...fieldProps}
                  select
                  label="Project cost"
                  name="project_cost"
                  value={form.project_cost ? 'true' : 'false'}
                  onChange={handleChange}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </TextField>
                <FormControlLabel
                  control={
                    <Checkbox name="paid" checked={form.paid} onChange={handleChange} size="medium" />
                  }
                  label="Paid"
                  sx={{
                    m: 0,
                    flexShrink: 0,
                    alignSelf: { xs: 'flex-start', sm: 'center' },
                    '& .MuiFormControlLabel-label': { typography: 'body2' }
                  }}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                {...fieldProps}
                label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                multiline
                minRows={2}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Stack spacing={1}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Button component="label" variant="outlined" size="medium" type="button">
                    Upload invoice (PDF)
                    <input
                      type="file"
                      accept="application/pdf"
                      hidden
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;

                        const fileExt = file.name.split('.').pop();
                        const fileName = `${Date.now()}.${fileExt}`;
                        const filePath = `invoices/${fileName}`;

                        const { error: uploadError } = await supabase.storage
                          .from('invoices')
                          .upload(filePath, file);

                        if (uploadError) {
                          toast.error('Error uploading invoice: ' + uploadError.message);
                        } else {
                          toast.success('Invoice uploaded!');
                          setForm((prev) => ({ ...prev, invoice_url: filePath }));
                        }
                      }}
                    />
                  </Button>
                  <Button type="submit" variant="contained" size="medium">
                    Save transaction
                  </Button>
                </Stack>
                {form.invoice_url ? (
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
                    File: {form.invoice_url}
                  </Typography>
                ) : null}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          <Typography variant="h6" component="h3" fontWeight={700}>
            Transaction history
          </Typography>
          <Button size="small" variant="text" onClick={() => setSortAsc(!sortAsc)}>
            {sortAsc ? 'Oldest first' : 'Newest first'} · tap to toggle
          </Button>
        </Stack>

        <List disablePadding sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          {paginatedTransactions.map((tx, index) => {
            const descRaw = tx.description;
            const descStr =
              descRaw != null && descRaw !== '' ? String(descRaw) : '';
            return (
              <React.Fragment key={tx.id}>
                {index > 0 ? <Divider component="li" /> : null}
                <ListItem disablePadding component="li">
                  <ListItemButton
                    onClick={() => setDetailTx(tx)}
                    sx={{
                      py: 1.5,
                      alignItems: 'flex-start',
                      bgcolor: !tx.paid ? '#ffe6e6' : index % 2 === 0 ? '#f9f9f9' : 'white',
                      '&:hover': { bgcolor: !tx.paid ? '#ffd6d6' : 'action.hover' }
                    }}
                  >
                    <ListItemText
                      disableTypography
                      primary={
                        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                          <Typography component="span" variant="body2" fontWeight={600}>
                            {tx.payment_date || '—'}
                          </Typography>
                          <Typography component="span" variant="body2" color="text.secondary">
                            {tx.from_who} → {tx.to_who}
                          </Typography>
                          <Typography component="span" variant="body2" fontWeight={600}>
                            ${tx.amount ?? '—'}
                          </Typography>
                          <Chip
                            size="small"
                            label={tx.paid ? 'Paid' : 'Unpaid'}
                            color={tx.paid ? 'success' : 'error'}
                            variant={tx.paid ? 'filled' : 'outlined'}
                            sx={{ height: 22 }}
                          />
                          {tx.invoice_url ? (
                            <Chip size="small" label="PDF" variant="outlined" sx={{ height: 22 }} />
                          ) : null}
                        </Stack>
                      }
                      secondary={
                        descStr ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                            {descStr.length > 120 ? `${descStr.slice(0, 120)}…` : descStr}
                          </Typography>
                        ) : null
                      }
                    />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>

        {paginatedTransactions.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No transactions yet.
          </Typography>
        )}

        <Dialog
          open={Boolean(detailTx)}
          onClose={() => setDetailTx(null)}
          maxWidth="md"
          fullWidth
          scroll="paper"
          aria-labelledby="tx-detail-title"
        >
          {detailTx && (
            <>
              <DialogTitle id="tx-detail-title">
                Transaction · {detailTx.payment_date || 'No date'}
              </DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">From</Typography>
                      <Typography variant="body1">{detailTx.from_who || '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">To</Typography>
                      <Typography variant="body1">{detailTx.to_who || '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">Amount</Typography>
                      <Typography variant="body1">${detailTx.amount ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">Wise fee</Typography>
                      <Typography variant="body1">${detailTx.wise_transaction_amount ?? '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">Project cost</Typography>
                      <Typography variant="body1">{detailTx.project_cost ? 'Yes' : 'No'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">Paid</Typography>
                      <Typography variant="body1">{detailTx.paid ? 'Yes' : 'No'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary">Description</Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {detailTx.description != null && detailTx.description !== ''
                          ? String(detailTx.description)
                          : '—'}
                      </Typography>
                    </Grid>
                  </Grid>

                  {detailTx.invoice_url ? (
                    <Box>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">Invoice preview</Typography>
                        <Link
                          href={invoicePublicUrl(detailTx.invoice_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                        >
                          Open in new tab
                        </Link>
                      </Stack>
                      <Box
                        sx={{
                          width: '100%',
                          height: { xs: 360, sm: 480 },
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                          bgcolor: 'grey.100'
                        }}
                      >
                        <Box
                          component="iframe"
                          title="Invoice PDF preview"
                          src={`${invoicePublicUrl(detailTx.invoice_url)}#view=FitH`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            display: 'block'
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        If the preview is blank, use “Open in new tab” (some browsers block embedded PDFs).
                      </Typography>
                    </Box>
                  ) : null}
                </Stack>
              </DialogContent>
              <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, py: 2, justifyContent: 'flex-start' }}>
                <Button size="medium" onClick={() => setDetailTx(null)}>
                  Close
                </Button>
                <Button
                  size="medium"
                  variant="outlined"
                  onClick={() => {
                    setForm({
                      from_who: detailTx.from_who || '',
                      to_who: detailTx.to_who || '',
                      amount: detailTx.amount != null ? String(detailTx.amount) : '',
                      wise_transaction_amount:
                        detailTx.wise_transaction_amount != null ? String(detailTx.wise_transaction_amount) : '',
                      payment_date: detailTx.payment_date || '',
                      project_cost: !!detailTx.project_cost,
                      paid: !!detailTx.paid,
                      description: detailTx.description || '',
                      invoice_url: detailTx.invoice_url || ''
                    });
                    setSelectedId(detailTx.id);
                    setIsEditing(true);
                    setDetailTx(null);
                  }}
                >
                  Edit
                </Button>
                <Button
                  size="medium"
                  color="error"
                  variant="outlined"
                  onClick={async () => {
                    const confirm = window.confirm('Delete this transaction?');
                    if (!confirm) return;
                    const { error } = await supabase.from('transactions').delete().eq('id', detailTx.id);
                    if (error) toast.error('Error deleting transaction: ' + error.message);
                    else {
                      toast.success('Transaction deleted.');
                      setDetailTx(null);
                      if (selectedId === detailTx.id) {
                        setSelectedId(null);
                        setIsEditing(false);
                      }
                      const { data } = await supabase.from('transactions').select('*');
                      setTransactions(data);
                    }
                  }}
                >
                  Delete
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        <Box
          sx={{
            mt: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" component="span">
              Show:
            </Typography>
            <TextField
              select
              size="small"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              sx={{ minWidth: 88 }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box component="span" sx={{ typography: 'body2' }}>
              Page {currentPage} of {totalPages}
              {sortedTransactions.length > 0 && (
                <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                  ({sortedTransactions.length} total)
                </Box>
              )}
            </Box>
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
            >
              Prev
            </Button>
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Paper>
    </Stack>
  );
}
